import React from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, RefreshCcw } from "lucide-react";
import { ErrorState } from "./ErrorState";
import { Button } from "./Button";
import { ContentContainer } from "./ContentContainer";

/**
 * ErrorBoundary — root-level React error boundary.
 *
 * Wraps the entire app. When a downstream render throws, renders a
 * graceful DS `ErrorState` with:
 *   * Retry action (re-render subtree by resetting boundary state)
 *   * Support-contact link
 *   * Console log with stack + component stack for debugging
 *
 * Does NOT swallow errors silently. Every catch writes to `console.error`.
 */

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Uncaught render error:", error, info?.componentStack || "");
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const errMsg = this.state.error?.message || "An unexpected error occurred while rendering this page.";
    return (
      <ContentContainer data-testid="app-error-boundary" width="narrow">
        <ErrorState
          title="Something went wrong"
          description={errMsg}
          data-testid="app-error-boundary-state"
          action={
            <Link
              to="/"
              onClick={this.reset}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-[var(--ds-radius-md)] bg-[var(--ds-color-surface)] border border-[var(--ds-color-border-strong)] text-[13px] font-medium text-[var(--ds-color-ink)] hover:bg-[var(--ds-color-surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)]"
              data-testid="app-error-boundary-home"
            >
              Return home
            </Link>
          }
          retry={this.reset}
        />
      </ContentContainer>
    );
  }
}

export default ErrorBoundary;
